import { isAxiosError, isCancel } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { ApiError } from 'types/api/ApiError';
import {
  QuotingPortals,
  SharepointLoadBuildingSources,
} from 'types/enums/Integrations';
import { SuggestedLoad } from 'types/suggestions/LoadBuildingSuggestions';
import { SuggestionSourceCategories } from 'types/suggestions/QuoteSuggestions';
import captureException from 'utils/captureException';

export type CreateLoadBuildingSuggestionRequest = {
  sourceCategory: SuggestionSourceCategories;
  source: QuotingPortals | SharepointLoadBuildingSources;
  sourceExternalID: string;
  sourceURL: string;
  suggested: SuggestedLoad;
};

export type CreateSharepointLoadBuildingSuggestionRequest = Omit<
  CreateLoadBuildingSuggestionRequest,
  'source' | 'sourceCategory'
> & {
  sourceCategory: SuggestionSourceCategories.Sharepoint;
  source: string;
};

export type CreateLoadBuildingSuggestionResponse = {
  suggestionID: number;
  suggested?: SuggestedLoad;
};

/**
 * Sends load building suggestion parsed from FE to be API to:
 * 1. Resolve raw values like customer & locations to TMS values
 * 2. Capture suggestion in metrics
 * Unlike quote requests where customer ID is important but not critical,
 * load building suggestions require a valid customer & stop location IDs to be created, hence the longer timeout.
 * @param data
 * @returns
 */
export async function createLoadBuildingSuggestion(
  data:
    | CreateLoadBuildingSuggestionRequest
    | CreateSharepointLoadBuildingSuggestionRequest
): Promise<Result<CreateLoadBuildingSuggestionResponse, ApiError>> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

  try {
    const response = await axios.post<CreateLoadBuildingSuggestionResponse>(
      '/suggestions/quoting-portal/load-building',
      data,
      {
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);
    return ok(response.data);
  } catch (error) {
    clearTimeout(timeoutId);
    captureException(error, { functionName: 'createLoadBuildingSuggestion' });

    if (isCancel(error)) {
      return err({ message: 'Request timed out after 10 seconds' });
    }

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to create load building suggestion' });
    }

    return err({
      message:
        error.response?.data.message ||
        'Failed to create load building suggestion',
    });
  }
}
